import React, { useState, useEffect } from 'react';
import progressReportService from '../../services/progressReportService';
import CourseStatusChart from './CourseStatusChart';
import CompletionRateChart from './CompletionRateChart';

interface CustomerProgressReportProps {
  customerId: string;
}

/**
 * Component for displaying a customer-specific progress report
 */
const CustomerProgressReport: React.FC<CustomerProgressReportProps> = ({ customerId }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const data = await progressReportService.getCustomerProgressReport(customerId);
        setReport(data);
        setError(null);
      } catch (err) {
        setError('Failed to load customer progress report. Please try again later.');
        console.error('Error fetching customer progress report:', err);
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchReport();
    }
  }, [customerId]);

  if (!customerId) {
    return <div>No customer ID provided.</div>;
  }

  if (loading) {
    return <div>Loading customer progress report...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  if (!report || !report.report) {
    return <div>No report data available for this customer.</div>;
  }

  const { customer, users, summary, generatedAt } = report.report;

  return (
    <div>
      <h2>Progress Report: {customer.customerName}</h2>
      <p>Generated at: {new Date(generatedAt).toLocaleString()}</p>
      <p>Customer Number: {customer.customerNumber}</p>
      {customer.primaryContactEmail && (
        <p>Primary Contact: {customer.primaryContactEmail}</p>
      )}

      <div style={{ marginBottom: '24px' }}>
        <h3>Summary</h3>
        
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          marginBottom: '24px', 
          gap: '20px'
        }}>
          <div style={{ 
            flex: '1 1 400px', 
            minWidth: '300px',
            marginBottom: '16px'
          }}>
            <h4>Course Status Distribution</h4>
            <div style={{ 
              height: '250px',
              border: '1px solid #eee',
              borderRadius: '4px',
              padding: '8px'
            }}>
              <CourseStatusChart 
                completedCourses={summary.completedCourses}
                inProgressCourses={summary.inProgressCourses}
                notStartedCourses={summary.notStartedCourses}
                notCompletedCourses={summary.notCompletedCourses || 0}
              />
            </div>
          </div>
          <div style={{ 
            flex: '1 1 400px', 
            minWidth: '300px',
            marginBottom: '16px'
          }}>
            <h4>Overall Completion Rate</h4>
            <div style={{ 
              height: '250px',
              border: '1px solid #eee',
              borderRadius: '4px',
              padding: '8px'
            }}>
              <CompletionRateChart 
                completionRate={summary.completionRate}
              />
            </div>
          </div>
        </div>
        
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Total Users</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Total Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Completed Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>In Progress Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Not Started Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Not Completed Courses</th>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Completion Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.totalUsers}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.totalCourses}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.completedCourses}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.inProgressCourses}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.notStartedCourses}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.notCompletedCourses || 0}</td>
              <td style={{ border: '1px solid #ddd', padding: '8px' }}>{summary.completionRate.toString().replace(',', '.')}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div>
        <h3>User Progress</h3>
        {users.length === 0 ? (
          <p>No users found for this customer.</p>
        ) : (
          users.map((user: any, index: number) => (
            <div key={index} style={{ marginBottom: '16px', padding: '16px', border: '1px solid #ddd', borderRadius: '4px' }}>
              <h4>{user.username}</h4>
              <p>Email: {user.email}</p>
              
              <div style={{ marginTop: '8px' }}>
                <h5>Course Progress</h5>
                {user.courses.length === 0 ? (
                  <p>No courses assigned to this user.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Course Title</th>
                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {user.courses.map((course: any, courseIndex: number) => (
                        <tr key={courseIndex}>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>{course.courseTitle}</td>
                          <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                            {course.status === 'completed' ? 'Completed' : 
                             course.status === 'in_progress' ? 'In Progress' : 
                             course.status === 'not_completed' ? 'Not Completed' : 'Not Started'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CustomerProgressReport;
